// src/pages/SetPassword.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import StepBar from "../components/StepBar.jsx";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

export default function SetPassword() {
  const seo = seoPresets["/login"];
  const { setPasswordName, me, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const next = params.get("next") || "/pricing?locked=1";
  const inviteCode = params.get("invite") || "";

  // Only allow users with verified email to be here
  useEffect(() => {
    if (authLoading) return;
    const verified = !!me?.user?.email_verified;
    if (!verified) nav("/signup", { replace: true });
  }, [authLoading, me, nav]);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (formLoading) return;
    setError("");

    if (!password || password.length < 8) {
      return setError("Password must be at least 8 characters.");
    }
    if (password !== confirm) {
      return setError("Passwords do not match.");
    }

    setFormLoading(true);
    try {
      await setPasswordName({ name: name || null, password });

      if (inviteCode) {
        try {
          await fetch("https://api.musicbase.io/api/referrals/claim", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ inviteCode }),
          });
        } catch (claimErr) {
          console.warn("Failed to claim invite credits", claimErr);
        }
      }

      nav(next, { replace: true });
    } catch (e2) {
      setError(e2?.message || "Failed to set password.");
    } finally {
      setFormLoading(false);
    }
  }


  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
      <div className="auth-wrap">
        <style>{css}</style>

        <div className="auth-grid">
          {/* LEFT: password form */}
          <section className="auth-main">
            <header className="auth-header">
              <Link to="/" className="brand-link">
                <div className="logo">
                  <img
                    className="brand-icon"
                    src="https://imgur.com/ef4tlNy.png"
                    alt=""
                    aria-hidden
                  />
                </div>
                <div className="brand-names">
                  <span className="brand-top">MusicBase</span>
                  <span className="brand-sub">playlist outreach</span>
                </div>
              </Link>

              <div className="auth-switch">
                <button
                  type="button"
                  className="auth-switch-btn"
                  onClick={() => nav("/login")}
                >
                  Log in
                </button>
                <button type="button" className="auth-switch-btn active">
                  Sign up
                </button>
              </div>
            </header>

            <div className="auth-body">
              {/* STEP BAR ROW (TOP) */}
              <div className="auth-stephead">
                <div className="auth-stepbar">
                  <StepBar
                    steps={["Email", "Verify", "Password"]}
                    current={2}
                  />
                </div>

                <div className="auth-copy">
                  <h1 className="auth-title">Secure your account</h1>
                  <p className="auth-subtitle">
                    Set a password and tell us your name to finish creating your
                    account.
                  </p>
                </div>
              </div>

              {/* FORM */}
              <form onSubmit={onSubmit} className="form form-grid" noValidate>
                {/* Left column: name */}
                <div className="stack">
                  <div className="field">
                    <input
                      className="input name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder=" "
                      autoComplete="name"
                      maxLength={80}
                      autoCapitalize="words"
                      spellCheck="false"
                    />
                    <label>Name (shown on profile)</label>
                  </div>
                </div>

                {/* Right column: passwords */}
                <div className="stack">
                  <div className="field">
                    <div className="pw-wrap">
                      <input
                        className="input pw"
                        type={show ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder=" "
                        autoComplete="new-password"
                        spellCheck="false"
                      />
                      <label>Password</label>
                      <button
                        type="button"
                        onClick={() => setShow((s) => !s)}
                        className={"showpw" + (show ? " on" : "")}
                        aria-label={show ? "Hide password" : "Show password"}
                      >
                        {show ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  <div className="field">
                    <input
                      className="input"
                      type={show ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder=" "
                      autoComplete="new-password"
                      spellCheck="false"
                    />
                    <label>Confirm password</label>
                  </div>
                </div>

                {error && (
                  <div className="error" role="alert">
                    {error}
                  </div>
                )}

                <div className="actions">
                  <button type="submit" disabled={formLoading} className="submit">
                    {formLoading ? "Saving…" : "Continue"}
                  </button>
                </div>
              </form>

              <p className="auth-footnote" style={{ marginTop: 14 }}>
                Already set this up? <Link to="/login">Log in</Link>.
              </p>
            </div>
          </section>

          {/* RIGHT: same campaign preview as Signup */}
          <aside className="auth-aside" aria-hidden="true">
            <div className="hero-shell">
              <div className="hero-pill">
                <span className="hero-dot" />
                <span>Campaign preview</span>
              </div>

              <h2 className="hero-title">
                See delivered and replies in one view.
              </h2>
              <p className="hero-text">
                As your campaigns run, MusicBase shows how many emails were
                delivered, bounced, or got a reply — the same metrics you see in
                your Campaigns table.
              </p>

              <div className="hero-card">
                <div className="hero-card-header">
                  <div className="hero-avatar">MB</div>
                  <div className="hero-card-text">
                    <div className="hero-card-title">Indie Pitch — Sample</div>
                    <div className="hero-card-sub">
                      Campaign overview · preview only
                    </div>
                  </div>
                </div>

                <div className="hero-rows">
                  <div className="hero-row">
                    <span className="hero-label">Delivered</span>
                    <span className="hero-value">54</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Replied</span>
                    <span className="hero-value">29</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Bounced</span>
                    <span className="hero-value">2</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Spam</span>
                    <span className="hero-value">1</span>
                  </div>
                </div>
              </div>

              <div className="hero-metrics">
                <div>
                  <div className="hero-metric-number">Outreach stats</div>
                  <div className="hero-metric-label">
                    Delivered, bounced and replied counts for every campaign.
                  </div>
                </div>
                <div>
                  <div className="hero-metric-number">Your own inbox</div>
                  <div className="hero-metric-label">
                    Connect Gmail or Outlook so the data comes from your mailbox,
                    not guesses.
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

const css = `
:root{
  --bg:#f5f4ff;
  --surface:#ffffff;
  --surface-soft:#f9fafb;
  --muted:#6b7280;
  --text:#0b0b0c;
  --accent:#7c5cff;
  --accent-2:#00d4ff;
  --ring:rgba(124,92,255,.25);
  --border:#e5e7eb;
  --shadow:0 24px 70px rgba(15,23,42,.14);
}

/* keep page scrollable / centered */
html, body {
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-behavior: smooth;
}

*{ box-sizing:border-box; }

.auth-wrap{
  min-height:100dvh;
  background:
    radial-gradient(1100px 650px at -10% -10%, rgba(124,92,255,.18), transparent 55%),
    radial-gradient(1100px 650px at 110% 0%, rgba(0,212,255,.18), transparent 55%),
    linear-gradient(#ffffff, #f9fafb);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:32px 16px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.auth-grid{
  max-width:1120px;
  background:var(--surface);
  border-radius:24px;
  box-shadow:var(--shadow);
  border:1px solid var(--border);
  display:grid;
  grid-template-columns:minmax(0,440px) minmax(0,1fr);
  overflow:hidden;
}

/* LEFT SIDE */
.auth-main{
  padding:28px 32px 26px;
  display:flex;
  flex-direction:column;
  background:linear-gradient(180deg,#ffffff,#f9fafb);
}

.auth-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:16px;
  margin-bottom:24px;
}

.brand-link{
  display:flex;
  align-items:center;
  gap:12px;
  text-decoration:none;
  color:inherit;
}

.logo{
  width:40px;
  height:40px;
  border-radius:14px;
  display:grid;
  place-items:center;
}

.brand-names{
  display:flex;
  flex-direction:column;
  line-height:1.05;
  align-items:flex-start;
  text-align:left;
}

.brand-top{
  font-weight:800;
  letter-spacing:.03em;
  font-size:15px;
}

.brand-sub{
  font-size:11px;
  text-transform:uppercase;
  letter-spacing:.16em;
  color:var(--muted);
}

/* toggle */
.auth-switch{
  display:inline-flex;
  padding:4px;
  border-radius:999px;
  background:#f3f4ff;
  border:1px solid #e0e7ff;
}
.auth-switch-btn{
  border:none;
  background:transparent;
  color:#6b7280;
  padding:6px 14px;
  border-radius:999px;
  font-size:12px;
  font-weight:600;
  cursor:pointer;
}
.auth-switch-btn.active{
  background:#111827;
  color:#f9fafb;
}

/* body + stepbar */
.auth-body{
  flex:1;
  display:flex;
  flex-direction:column;
}

.auth-stephead{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:10px;
  margin-bottom:18px;
}

.auth-stepbar{
  width:100%;
  display:flex;
  justify-content:center;
}
.auth-stepbar > *{
  max-width:280px;
}

.auth-copy{
  max-width:280px;
  text-align:center;
}

.auth-title{
  margin:0 0 6px 0;
  font-size:30px;
  line-height:1.1;
  letter-spacing:-.02em;
  color:var(--text);
}
.auth-subtitle{
  margin:0;
  font-size:14px;
  color:var(--muted);
}

/* Generic form + fields (same base as signup) */
.form{display:grid; gap:12px;}

.field{position:relative;}
.field input{
  width:100%;
  padding:16px 90px 10px 14px;
  border-radius:14px;
  border:1px solid var(--border);
  background:#ffffff;
  color:var(--text);
  font-size:14px;
  outline:none;
}
.field input::placeholder{color:transparent;}
.field label{
  position:absolute;
  left:14px;
  top:13px;
  pointer-events:none;
  color:var(--muted);
  background:#ffffff;
  padding:0 .25em;
  border-radius:999px;
  transition:all .16s ease;
  font-size:13px;
}
.field input:focus{
  box-shadow:0 0 0 5px var(--ring);
  border-color:#c4b5fd;
}
.field input:focus + label,
.field.has-value label{
  top:-8px;
  font-size:11px;
  color:#4b5563;
}

/* error + buttons */
.error{
  background:#fef2f2;
  border:1px solid #fecaca;
  color:#7f1d1d;
  padding:10px 12px;
  border-radius:12px;
  font-size:13px;
}

.submit{
  margin-top:4px;
  padding:13px 14px;
  border-radius:14px;
  font-weight:800;
  letter-spacing:.03em;
  border:none;
  cursor:pointer;
  background:linear-gradient(90deg,var(--accent),var(--accent-2));
  color:#fff;
  box-shadow:0 10px 40px rgba(124,92,255,.35);
}
.submit:disabled{
  opacity:.7;
  cursor:default;
  box-shadow:none;
}

.auth-footnote{
  margin:10px 0 0;
  font-size:11px;
  color:#9ca3af;
}
.auth-footnote a{
  color:#7c5cff;
  text-decoration:underline;
}

/* RIGHT SIDE */
.auth-aside{
  padding:32px 40px;
  background:linear-gradient(135deg,#eef2ff,#fdfbff);
  display:flex;
  align-items:center;
  justify-content:center;
}
.hero-shell{
  max-width:380px;
}
.hero-pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 10px;
  border-radius:999px;
  background:#111827;
  color:#e5e7eb;
  font-size:11px;
  font-weight:600;
  text-transform:uppercase;
  letter-spacing:.16em;
  margin-bottom:10px;
}
.hero-dot{
  width:7px; height:7px; border-radius:999px;
  background:linear-gradient(135deg,var(--accent),var(--accent-2));
}
.hero-title{
  margin:0 0 8px;
  font-size:24px;
  line-height:1.25;
  letter-spacing:-.02em;
  color:#020617;
}
.hero-text{
  margin:0 0 18px;
  font-size:14px;
  color:#4b5563;
}

/* screenshot-style card */
.hero-card{
  background:#ffffff;
  border-radius:18px;
  padding:16px 16px 14px;
  border:1px solid rgba(148,163,184,.55);
  box-shadow:0 22px 60px rgba(15,23,42,.16);
  margin-bottom:18px;
}
.hero-card-header{
  display:flex;
  align-items:center;
  gap:10px;
  margin-bottom:10px;
}
.hero-avatar{
  width:34px; height:34px;
  border-radius:12px;
  background:linear-gradient(90deg,var(--accent),var(--accent-2));
  display:grid; place-items:center;
  font-size:13px; font-weight:800; color:#fff;
}
.hero-card-text{
  display:flex;
  flex-direction:column;
}
.hero-card-title{
  font-size:14px;
  font-weight:600;
  color:#020617;
}
.hero-card-sub{
  font-size:12px;
  color:#6b7280;
}
.hero-rows{
  display:grid;
  gap:6px;
}
.hero-row{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:12px;
}
.hero-label{
  width:72px;
  color:#6b7280;
}
.hero-value{
  color:#111827;
}

/* metrics */
.hero-metrics{
  display:flex;
  flex-wrap:wrap;
  gap:18px;
}
.hero-metric-number{
  font-size:18px;
  font-weight:800;
  color:#020617;
}
.hero-metric-label{
  font-size:12px;
  color:#4b5563;
  max-width:220px;
}

/* Extra layout for password form */
.form-grid{
  display:grid;
  gap:16px;
  grid-template-columns: 1fr 1fr;
  align-items:start;
}
.stack{ display:grid; gap:14px; }
.actions{ grid-column: 1 / -1; }

@media (max-width: 900px){
  .form-grid{ grid-template-columns: 1fr; }
}

/* Inputs specifically for password form */
.input{
  width:100%;
  min-height:54px;
  padding:16px 14px;
  border-radius:14px;
  border:1px solid var(--border);
  background:#fff;
  color:var(--text);
  font-size:16px;
  outline:none;
  overflow-x:auto;
  white-space:nowrap;
}
.input::-webkit-scrollbar{ display:none; }
.input:focus{
  box-shadow:0 0 0 6px var(--ring);
  border-color:#cfd7ff;
}

.input.name{ letter-spacing:.2px; }

/* Floating labels for .input */
.field label{
  left:12px;
  top:50%;
  transform:translateY(-50%);
}
.input:not(:placeholder-shown) + label,
.input:focus + label{
  top:-9px;
  font-size:12px;
  color:#3a4150;
}

/* Password with Show button */
.pw-wrap{ position:relative; }
.input.pw{ padding-right:100px; }
.showpw{
  position:absolute;
  right:6px;
  top:6px;
  padding:10px 12px;
  font-size:12px;
  border-radius:10px;
  border:1px solid var(--border);
  background:#fff;
  color:#3a4150;
  cursor:pointer;
}
.showpw.on{
  background:#f3f4ff;
  border-color:#d6d9ff;
  color:#2b1a88;
}

/* Responsive */
@media (max-width: 960px){
  .auth-grid{
    border-radius:0px;
  }
  body {
    overflow-y: auto;
  }
  .auth-grid{
    grid-template-columns:1fr;
    max-width:560px;
  }
  .auth-aside{
    /* keep password form first on mobile, like signup */
    padding:24px 24px 12px;
  }
  .auth-main{
    padding:24px 22px 24px;
  }
  .auth-copy{
    max-width:none;
  }
}

@media (max-width: 560px){
  .auth-wrap{padding:0px;}
  .auth-header{align-items:flex-start;}
  .auth-switch{align-self:flex-end;}
  .auth-copy{
    max-width:none;
    text-align:left;
  }
}
`;